/**
 * A minimal typed event emitter. The audio engines are imperative and live
 * outside the reactive graph; this is the seam where their events cross into
 * Solid signals.
 */

export type EventMap = Record<string, unknown[]>;

export type Unsubscribe = () => void;

/**
 * One map holds listeners for every key, so the stored element type has to be
 * erased. The two casts below are the only ones; the public API stays typed.
 */
type ErasedListener = (...args: never[]) => void;

export class Emitter<M extends EventMap = EventMap> {
  #listeners = new Map<keyof M, Set<ErasedListener>>();

  on<K extends keyof M>(key: K, fn: (...args: M[K]) => void): Unsubscribe {
    let set = this.#listeners.get(key);
    if (!set) {
      set = new Set();
      this.#listeners.set(key, set);
    }
    set.add(fn as unknown as ErasedListener);
    return () => {
      this.off(key, fn);
    };
  }

  off<K extends keyof M>(key: K, fn: (...args: M[K]) => void): void {
    this.#listeners.get(key)?.delete(fn as unknown as ErasedListener);
  }

  /** A throwing listener is reported and skipped; the rest still run. */
  emit<K extends keyof M>(key: K, ...args: M[K]): void {
    const set = this.#listeners.get(key);
    if (!set) return;
    for (const fn of set) {
      try {
        (fn as unknown as (...a: M[K]) => void)(...args);
      } catch (e) {
        console.error(e);
      }
    }
  }
}
